import React, { useEffect, useState } from 'react'
import {
	Button, DatePicker, Drawer, Popover, Row, Space
} from 'antd'
import { useTranslation } from 'react-i18next'
import { CloseOutlined } from '@ant-design/icons'
import moment from 'moment'
// eslint-disable-next-line import/no-cycle
import { useDispatch, useSelector } from 'react-redux'
// eslint-disable-next-line import/no-cycle
import PayslipDrawerContent from './PayslipDrawerContent'
// eslint-disable-next-line import/no-cycle
import { RootState } from '../../../reducers'
// eslint-disable-next-line import/no-cycle
import {
	createPayslipForUser, finishPayslipForUser, getPayslipForUser, PayslipStatus, resetPayslipForUser
} from '../../../reducers/payslips/actions'

interface PayslipDrawerContentProps {
	userID: number
	onClose: () => void
	open: boolean,
	onRefreshList: () => void,
	initialMonth?: moment.Moment,
}

const PayslipDrawer: React.FC<PayslipDrawerContentProps> = ({
	userID, onClose, open, onRefreshList, initialMonth
}) => {
	const { t } = useTranslation('pages')
	const dispatch = useDispatch()

	const payslip = useSelector((state: RootState) => state.payslips.payslipDetail.data)

	const [selectedMonth, setSelectedMonth] = useState(initialMonth ? moment(initialMonth).startOf('month') : moment().startOf('month'))

	useEffect(() => {
		if (open && initialMonth) {
			setSelectedMonth(moment(initialMonth).startOf('month'))
		}
	}, [open, initialMonth])

	const [isLoading, setIsLoading] = useState(false)

	const [paycheckFromManager, setPaycheckFromManager] = React.useState<number | null>(
		payslip?.paycheckFromManager ? Number(payslip.paycheckFromManager) : 0
	)

	const handleCreatePayslip = () => {
		if (!userID || !selectedMonth) return

		setIsLoading(true)

		dispatch(createPayslipForUser(
			userID,
			{
				month: selectedMonth.startOf('month').toISOString(),
				paycheckFromManager: paycheckFromManager || 0
			},
			() => {
				dispatch(getPayslipForUser(userID, selectedMonth.startOf('month').toISOString())) // refresh state
				setIsLoading(false)
				onRefreshList()
			},
			() => {
				setIsLoading(false)
			}
		))
	}

	const handleResetPayslip = () => {
		if (!userID || !selectedMonth) return

		setIsLoading(true)

		dispatch(resetPayslipForUser(
			userID,
			{
				month: selectedMonth.startOf('month').toISOString()
			},
			() => {
				dispatch(getPayslipForUser(userID, selectedMonth.startOf('month').toISOString())) // refresh
				setIsLoading(false)
				onRefreshList()
			},
			() => {
				setIsLoading(false)
			}
		))
	}


	const handleFinishPayslip = () => {
		if (!userID || !selectedMonth) return

		setIsLoading(true)

		dispatch(finishPayslipForUser(
			userID,
			{
				month: selectedMonth.startOf('month').toISOString()
			},
			() => {
				dispatch(getPayslipForUser(userID, selectedMonth.startOf('month').toISOString())) // refresh state
				setIsLoading(false)
				onRefreshList()
			},
			() => {
				setIsLoading(false)
			}
		))
	}

	const hasErrors = (payslip?.errorMissingPriceCount ?? 0) > 0
		|| (payslip?.errorMissingFlatsCount ?? 0) > 0

	const errorContent = payslip && (
		<div className="text-sm">
			{payslip.errorMissingPriceCount > 0 && (
				<div>{t('pages:Chýbajúce ceny za byty')}: {payslip.errorMissingPriceCount}</div>
			)}
			{payslip.errorMissingFlatsCount > 0 && (
				<div>{t('pages:Chýbajúce počty bytov')}: {payslip.errorMissingFlatsCount}</div>
			)}
		</div>
	)

	const finalizeButton = (
		<Button
			className="mr-4 ml-4"
			type="primary"
			onClick={handleFinishPayslip}
			disabled={hasErrors}
			loading={isLoading}
		>
			{t('pages:Ukončiť')}
		</Button>
	)

	return (
		<Drawer
			title={
				<Row justify="space-between" align="middle" style={{ flexWrap: 'wrap', gap: 8 }}>
					<Space>
						<CloseOutlined style={{ color: '#aaa' }} onClick={() => onClose()} />
						<div>
							<span>{t('pages:Výplatná páska')}</span>
							<span className={'font-bold ml-4'}>{payslip?.user}</span>
							<span className={'ml-4 text-xs'} style={{ fontSize: '12px' }}>(ID: {userID})</span>
						</div>
					</Space>
					<Space style={{ marginLeft: 'auto' }}>
						<DatePicker
							picker="month"
							allowClear={false}
							value={moment(selectedMonth) as any} // anonymný objekt
							onChange={(val) => {
								if (val) {
									setSelectedMonth(moment(val)) // opäť konvertujeme na vlastný objekt
								}
							}}
						/>
						{
							payslip?.payslipID ? (
								<span>
									<Button
										type="primary"
										onClick={handleCreatePayslip}
										disabled={false}
										loading={isLoading}
									>
										{t('pages:Uložiť zmeny')}
									</Button>

									{payslip?.status === PayslipStatus.SAVED && (
										hasErrors ? (
											<Popover
												color="red"
												overlayInnerStyle={{ padding: '12px', borderRadius: '6px' }}
												placement="left"
												content={errorContent}
											>
												{finalizeButton}
											</Popover>
										) : (
											<Popover
												color="blue"
												overlayInnerStyle={{ padding: '12px', borderRadius: '6px' }}
												placement="left"
												content={
													<div className="text-sm">
														{t(
															'pages:Výplata označená ako „Ukončená“ sa považuje za vyplatenú a vykonané upratovania sa natrvalo priradia k tomuto mesiacu.'
														)}
													</div>
												}
											>
												{finalizeButton}
											</Popover>
										)
									)}

									{payslip?.status !== PayslipStatus.DRAFT && (
										<Popover
											color="red"
											overlayInnerStyle={{ padding: '12px', borderRadius: '6px' }}
											placement="left"
											content={
												<div className="text-sm">
													{t('pages:Resetovaním sa výplatná páska vymaže a bude potrebné ju znova vytvoriť.')}
												</div>
											}
										>
											<Button
												danger
												onClick={handleResetPayslip}
												loading={isLoading}
												className="ml-2"
											>
												{t('pages:Resetovať pásku')}
											</Button>
										</Popover>
									)}

								</span>

							) : (
								<Button
									type="primary"
									onClick={handleCreatePayslip}
									loading={isLoading}
								>
									{t('pages:Vytvoriť výplatnú pásku')} <span className={'font-bold ml-2'}>{selectedMonth.format('MMMM YYYY')}</span>
								</Button>
							)
						}
					</Space>
				</Row>
			}
			width={'min(90vw, 1500px)'}
			closeIcon={<></>}
			onClose={onClose}
			visible={open}
		>
			<PayslipDrawerContent
				userID={userID}
				selectedMonth={selectedMonth}
				paycheckFromManager={paycheckFromManager}
				setPaycheckFromManager={setPaycheckFromManager}
				isLoading={isLoading}
				onBlurSave={() => {
					if (paycheckFromManager !== payslip?.paycheckFromManager) {
						handleCreatePayslip()
					}
				}}
			/>
		</Drawer>
	)
}

export default PayslipDrawer
